
import { useState } from 'react'
import { useSiteMedia, useUploadMedia } from '@/hooks/useCMS'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { toast } from 'sonner'
import { Image, Upload, Plus } from 'lucide-react'
import { useRef } from 'react'

export const CMSMediaLibrary = () => {
  const { data: media, isLoading } = useSiteMedia()
  const uploadMedia = useUploadMedia()
  const [isUploadOpen, setIsUploadOpen] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (!files || files.length === 0) return

    for (const file of Array.from(files)) {
      try {
        await uploadMedia.mutateAsync({
          file,
          usageType: 'general',
          altText: file.name
        })
      } catch (error) {
        console.error('Upload failed:', error)
        toast.error(`Erreur lors du téléchargement de ${file.name}`)
      }
    }

    toast.success('Fichiers téléchargés avec succès!')
    setIsUploadOpen(false)
  }

  if (isLoading) {
    return <div>Chargement de la médiathèque...</div>
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Image className="w-5 h-5" />
          <p className="text-gray-600">Gérez vos images et médias</p>
        </div>
        
        <Dialog open={isUploadOpen} onOpenChange={setIsUploadOpen}>
          <DialogTrigger asChild>
            <Button className="bg-gradient-to-r from-pink-500 to-blue-500">
              <Plus className="w-4 h-4 mr-2" />
              Ajouter des médias
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Télécharger des médias</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Sélectionner des fichiers</Label>
                <Input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="mt-2"
                />
              </div>
              <Button 
                onClick={() => fileInputRef.current?.click()}
                variant="outline" 
                className="w-full"
              >
                <Upload className="w-4 h-4 mr-2" />
                Choisir des fichiers
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {media?.map((item) => (
          <div key={item.id} className="border rounded-lg overflow-hidden hover:shadow-md transition-shadow">
            <div className="aspect-square bg-gray-100 flex items-center justify-center">
              <img
                src={item.file_path}
                alt={item.alt_text || item.name}
                className="w-full h-full object-cover"
                onError={(e) => {
                  const target = e.target as HTMLImageElement
                  target.style.display = 'none'
                  target.nextElementSibling?.classList.remove('hidden')
                }}
              />
              <div className="hidden flex items-center justify-center">
                <Image className="w-8 h-8 text-gray-400" />
              </div>
            </div>
            <div className="p-3">
              <p className="text-sm font-medium truncate">{item.original_name}</p>
              <p className="text-xs text-gray-500">
                {item.file_size ? `${Math.round(item.file_size / 1024)} KB` : 'Taille inconnue'}
              </p>
              <p className="text-xs text-gray-500">{item.usage_type || 'Général'}</p>
            </div>
          </div>
        )) || (
          <div className="col-span-full text-center py-12">
            <Image className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">Aucun média trouvé</p>
            <Button 
              variant="outline" 
              className="mt-2"
              onClick={() => setIsUploadOpen(true)}
            >
              Télécharger votre premier média
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
